import React, { useEffect, useState } from "react";
import { connect } from "react-redux";
import {
  requestServerGet,
  requestServerPost,
  selectBrand,
  selectPage,
  updateGlobalSearch,
} from "../../actions";
import MainLayout from "../../components/layouts/main";
import Constants from "../../data/constants";
import { Table, Modal } from "antd";
import PageLoader from "../../components/PageLoader";
import { motion } from "framer-motion";
import LogFilter from "../../components/views/LogFilter";
import moment from "moment";
import { ClipboardList, CheckCircle, XCircle, Eye } from "lucide-react";
import Animations from "../../animations/Animations";
import {
  useProLanguage,
  PRO_TRANSLATIONS,
} from "../../global/ProLanguageContext";

const CATEGORY_COLORS = {
  create: { bg: "#ede9fe", color: "#7c3aed" },
  update: { bg: "#fef3c7", color: "#d97706" },
  edit: { bg: "#fef3c7", color: "#d97706" },
  delete: { bg: "#fee2e2", color: "#dc2626" },
  publish: { bg: "#dcfce7", color: "#16a34a" },
  upload: { bg: "#dbeafe", color: "#2563eb" },
  login: { bg: "#f0fdf4", color: "#15803d" },
  logout: { bg: "#fef9c3", color: "#a16207" },
};
const getCategoryStyle = (val) => {
  if (!val) return { bg: "#f1f5f9", color: "#64748b" };
  const key = Object.keys(CATEGORY_COLORS).find((k) =>
    val.toLowerCase().includes(k),
  );
  return key ? CATEGORY_COLORS[key] : { bg: "#f1f5f9", color: "#64748b" };
};

const CategoryBadge = ({ text }) => {
  const { bg, color } = getCategoryStyle(text);
  return (
    <span
      style={{
        display: "inline-block",
        padding: "2px 10px",
        borderRadius: 20,
        fontSize: 11,
        fontWeight: 700,
        letterSpacing: "0.04em",
        textTransform: "uppercase",
        background: bg,
        color,
      }}
    >
      {text || "—"}
    </span>
  );
};

function ActivityLogPage(props) {
  const [activityLogData, setActivityLogData] = useState({});
  const [modalVisible, setModalVisible] = useState(false);
  const [selectedResponse, setSelectedResponse] = useState(null);
  const [modalTitle, setModalTitle] = useState("");
  const [filter, setFilter] = useState(null);
  const [page, setPage] = useState(1);
  const [perPage, setPerPageage] = useState(20);
  const [totalRecords, setTotalRecords] = useState(0);
  const [loading, setLoading] = useState(false);

  const { lang } = useProLanguage();
  const t = (PRO_TRANSLATIONS[lang] || PRO_TRANSLATIONS.en).activityLog;

  const openModal = (data, title) => {
    setSelectedResponse(data);
    setModalTitle(title);
    setModalVisible(true);
  };

  const columns = [
    {
      title: t.colDate,
      dataIndex: "ActionDate",
      key: "ActionDate",
      width: 180,
      sorter: (a, b) => new Date(a.ActionDate) - new Date(b.ActionDate),
      defaultSortOrder: "descend",
      render: (text) => (
        <span style={{ fontSize: 12, color: "#64748b", whiteSpace: "nowrap" }}>
          {text ? moment(text).format("MMM D, YYYY · HH:mm") : "—"}
        </span>
      ),
    },
    {
      title: t.colUser,
      dataIndex: "ByUser",
      key: "ByUser",
      sorter: (a, b) => (a.ByUser || "").localeCompare(b.ByUser || ""),
      render: (text) => (
        <span style={{ fontWeight: 600, color: "#0f172a", fontSize: 13 }}>
          {text || "—"}
        </span>
      ),
    },
    {
      title: t.colCategory,
      dataIndex: "Function",
      key: "Function",
      sorter: (a, b) => (a.Function || "").localeCompare(b.Function || ""),
      render: (text) => <CategoryBadge text={text} />,
    },
    {
      title: t.colAction,
      dataIndex: "Action",
      key: "Action",
      sorter: (a, b) => (a.Action || "").localeCompare(b.Action || ""),
      render: (text) => (
        <span style={{ fontSize: 13, color: "#334155" }}>{text || "—"}</span>
      ),
    },
    {
      title: t.colParams,
      key: "Request",
      width: 100,
      render: (_, record) => (
        <button
          onClick={() => openModal(record.Params, t.modalParams)}
          style={{
            height: 28,
            padding: "0 10px",
            borderRadius: 6,
            background: "#f8fafc",
            border: "1px solid #e2e8f0",
            color: "#64748b",
            fontSize: 12,
            fontWeight: 500,
            cursor: "pointer",
            display: "inline-flex",
            alignItems: "center",
            gap: 4,
          }}
        >
          <Eye size={12} /> {t.viewBtn}
        </button>
      ),
    },
    {
      title: t.colResponse,
      key: "view",
      width: 110,
      render: (_, record) => (
        <button
          onClick={() => openModal(record.ResponseData, t.modalResponse)}
          style={{
            height: 28,
            padding: "0 10px",
            borderRadius: 6,
            background: "#f8fafc",
            border: "1px solid #e2e8f0",
            color: "#64748b",
            fontSize: 12,
            fontWeight: 500,
            cursor: "pointer",
            display: "inline-flex",
            alignItems: "center",
            gap: 4,
          }}
        >
          <Eye size={12} /> {t.viewBtn}
        </button>
      ),
    },
    {
      title: t.colResult,
      dataIndex: "Response",
      key: "Response",
      width: 100,
      sorter: (a, b) => (a.Response || "").localeCompare(b.Response || ""),
      render: (text) => {
        const ok = text === "success";
        return (
          <span
            style={{
              display: "inline-flex",
              alignItems: "center",
              gap: 5,
              padding: "3px 10px",
              borderRadius: 20,
              fontSize: 11,
              fontWeight: 700,
              textTransform: "uppercase",
              background: ok ? "#dcfce7" : "#fee2e2",
              color: ok ? "#16a34a" : "#dc2626",
            }}
          >
            {ok ? <CheckCircle size={12} /> : <XCircle size={12} />}
            {text || "—"}
          </span>
        );
      },
    },
  ];

  useEffect(() => {
    get7DaysLog();
  }, []);

  useEffect(() => {
    if (filter && filter.dateRange && filter.dateRange.length > 1) {
      const startDate = moment(filter.dateRange[0]).format("YYYY-MM-DD");
      const endDate = moment(filter.dateRange[1]).format("YYYY-MM-DD");
      const users = filter && filter.users ? filter.users.join(",") : "";
      console.log("filter " + startDate);
      console.log("filter " + endDate);

      getActivityLogsData({
        startdate: startDate,
        enddate: endDate,
        userids: users,
      });
    } else {
      get7DaysLog();
    }
  }, [filter, page]);

  const get7DaysLog = () => {
    const endDate = moment().format("YYYY-MM-DD");
    const startDate = moment().subtract(7, "days").format("YYYY-MM-DD");
    const users = filter && filter.users ? filter.users.join(",") : "";

    getActivityLogsData({
      startdate: startDate,
      enddate: endDate,
      perpage: perPage,
      page: page,
      userids: users,
    });
  };

  const getActivityLogsData = (params) => {
    const { usertoken, requestServerGet, selectedBrandData } = props;

    if (!selectedBrandData) return;

    setLoading(true);

    requestServerGet(
      "get_activity_logs",
      {
        brandid: selectedBrandData.BrandID,
        perpage: perPage,
        page: page,
        ...params,
      },
      null,
      (response) => {
        console.log("Activity Log " + JSON.stringify(response.data));
        // const options = response.data.map(data => {
        //     return { value: data.CampaignID, label: data.CampaignName }
        // })
        if (response.data.pagination) {
          console.log(
            "setTotalRecords " + response.data.pagination.totalrecords,
          );
          setTotalRecords(response.data.pagination.totalrecords);
        }

        setActivityLogData(response.data);
        setLoading(false);
        // this.setState({ campaigntDatas: options, campaignOptions: options })
      },
      usertoken,
    );
  };

  const onPageChange = (page, pageSize) => {
    setPage(page);
  };

  return (
    <MainLayout
      id={Constants.ACTIVITYLOG_PAGE}
      quietTitleBar={true}
      style={{ padding: 20 }}
      showTopBarBrandLogo={true}
      showSearchBar={true}
      renderBreadCrumb={() => [
        <span
          style={{
            fontSize: 14,
            fontWeight: 500,
            color: "#94a3b8",
            padding: "4px 8px",
            lineHeight: "22px",
          }}
        >
          {t.pageTitle || "Activity Log"}
        </span>,
      ]}
    >
      <motion.div {...Animations.PAGE_ENTER}>
        {loading && <PageLoader text={t.loading} />}

        {/* Page header */}
        <div
          style={{
            display: "flex",
            alignItems: "center",
            justifyContent: "space-between",
            background: "#fff",
            borderRadius: 16,
            padding: "20px 24px",
            border: "1px solid #e8ecf3",
            marginBottom: 20,
            boxShadow: "0 1px 3px rgba(0,0,0,0.04)",
          }}
        >
          <div style={{ display: "flex", alignItems: "center", gap: 16 }}>
            <div
              style={{
                width: 52,
                height: 52,
                borderRadius: 14,
                flexShrink: 0,
                background: "linear-gradient(135deg,#8b5cf6,#7c3aed)",
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
              }}
            >
              <ClipboardList size={26} color="#fff" />
            </div>
            <div>
              <div
                style={{
                  fontSize: 22,
                  fontWeight: 800,
                  color: "#0f172a",
                  lineHeight: 1.2,
                }}
              >
                {t.pageTitle}
              </div>
              <div style={{ fontSize: 13, color: "#94a3b8", marginTop: 3 }}>
                {t.pageSubtitle}
              </div>
            </div>
          </div>
          {totalRecords > 0 && (
            <div
              style={{
                fontSize: 13,
                color: "#64748b",
                background: "#f8fafc",
                border: "1px solid #f1f5f9",
                borderRadius: 8,
                padding: "6px 14px",
              }}
            >
              {t
                .events(totalRecords)
                .split(String(totalRecords))
                .map((part, i, arr) =>
                  i < arr.length - 1 ? (
                    <React.Fragment key={i}>
                      <span style={{ fontWeight: 700, color: "#7c3aed" }}>
                        {totalRecords}
                      </span>
                      {part}
                    </React.Fragment>
                  ) : (
                    <React.Fragment key={i}>{part}</React.Fragment>
                  ),
                )}
            </div>
          )}
        </div>

        {/* Filter card */}
        <div
          style={{
            background: "#fff",
            borderRadius: 16,
            border: "1px solid #f1f5f9",
            boxShadow: "0 1px 3px rgba(0,0,0,0.04)",
            marginBottom: 16,
            overflow: "hidden",
          }}
        >
          <div
            style={{
              display: "flex",
              alignItems: "center",
              gap: 10,
              padding: "12px 20px",
              borderBottom: "1px solid #f1f5f9",
              background: "#fafbfc",
            }}
          >
            <span
              style={{
                fontSize: 13,
                fontWeight: 700,
                color: "#0f172a",
                letterSpacing: "0.02em",
              }}
            >
              {t.filters}
            </span>
          </div>
          <div style={{ padding: "14px 20px" }}>
            <LogFilter onFilterChange={setFilter} parentProps={props} />
          </div>
        </div>

        {/* Table card */}
        <div
          style={{
            background: "#fff",
            borderRadius: 16,
            border: "1px solid #f1f5f9",
            boxShadow: "0 1px 3px rgba(0,0,0,0.04)",
            overflow: "hidden",
          }}
        >
          <Table
            columns={columns}
            dataSource={(activityLogData.activitylog || []).filter(
              (r) => r && r.ActionDate,
            )}
            rowKey={(_, index) => index}
            scroll={{ x: 800 }}
            pagination={{
              pageSize: perPage,
              defaultCurrent: page,
              total: totalRecords,
              onChange: onPageChange,
              style: { padding: "12px 20px" },
            }}
            size="middle"
            style={{ borderRadius: 0 }}
            rowClassName={(_, i) => (i % 2 === 1 ? "activity-row-alt" : "")}
          />
        </div>

        {/* Detail modal */}
        <Modal
          title={
            <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
              <Eye size={16} color="#7c3aed" />
              <span style={{ fontWeight: 700, color: "#0f172a" }}>
                {modalTitle}
              </span>
            </div>
          }
          open={modalVisible}
          onCancel={() => setModalVisible(false)}
          footer={null}
          width={620}
          centered
        >
          <div
            style={{
              background: "#0f172a",
              borderRadius: 10,
              padding: "16px 20px",
              maxHeight: 400,
              overflow: "auto",
              marginTop: 8,
            }}
          >
            <pre
              style={{
                whiteSpace: "pre-wrap",
                wordBreak: "break-all",
                fontSize: 12,
                color: "#e2e8f0",
                margin: 0,
                lineHeight: 1.6,
              }}
            >
              {JSON.stringify(selectedResponse, null, 2)}
            </pre>
          </div>
        </Modal>
      </motion.div>
    </MainLayout>
  );
}

const mapStateToProps = (state) => {
  return {
    userdata: state.userdata,
    usertoken: state.usertoken,
    usermaintoken: state.usermaintoken,
    globalSearch: state.globalSearch,
    selectedBrandData: state.selectedBrandData,
  };
};

export default connect(mapStateToProps, {
  updateGlobalSearch,
  requestServerGet,
  requestServerPost,
  selectBrand,
  selectPage,
})(ActivityLogPage);
